import type { CommentItem } from '@wlb/shared/types/comments';
import { toMentionToken } from '@wlb/shared/utils/mentions';

export const isCommentOwnedByCurrentUser = (
  comment: Pick<CommentItem, 'authorId' | 'profileUserGuid'>,
  currentUserId?: string | number | null,
  currentUserGuid?: string | null,
): boolean => {
  if (
    comment.authorId != null &&
    currentUserId != null &&
    String(comment.authorId) === String(currentUserId)
  ) {
    return true;
  }

  return Boolean(
    comment.profileUserGuid &&
      currentUserGuid &&
      comment.profileUserGuid.trim().toLowerCase() ===
        currentUserGuid.trim().toLowerCase(),
  );
};

export const resolveReplyComposerText = (
  comment: Pick<CommentItem, 'authorId' | 'profileUserGuid'>,
  mentionLabel: string,
  currentUserId?: string | number | null,
  currentUserGuid?: string | null,
): string => {
  if (isCommentOwnedByCurrentUser(comment, currentUserId, currentUserGuid)) {
    return '';
  }

  const mentionToken = toMentionToken(mentionLabel);
  return mentionToken ? `@${mentionToken} ` : '';
};

const backendCommentId = (comment: CommentItem): string =>
  comment.postCommentId != null ? String(comment.postCommentId) : comment.id;

/** Resolves the API ID of the exact comment or reply selected by the user. */
export const resolveReplyParentId = (
  comments: CommentItem[],
  target: CommentItem | null,
): string | undefined => {
  if (!target) return undefined;
  if (target.postCommentId != null) return String(target.postCommentId);

  for (const root of comments) {
    if (root.id === target.id) return backendCommentId(root);
    if ((root.replies ?? []).some(reply => reply.id === target.id)) {
      return backendCommentId(root);
    }
  }

  return target.id.startsWith('comment-') ? undefined : target.id;
};

/** Finds the top-level thread that owns a selected comment or reply. */
export const resolveReplyThreadId = (
  comments: CommentItem[],
  target: CommentItem | null,
): string | undefined => {
  if (!target) return undefined;

  for (const root of comments) {
    if (
      root.id === target.id ||
      (root.replies ?? []).some(reply => reply.id === target.id)
    ) {
      return root.id;
    }
  }

  return undefined;
};

/** Returns no nested rows until the user explicitly expands the thread. */
export const resolveVisibleReplies = (
  comment: CommentItem,
  expandedThreadIds: ReadonlySet<string>,
): CommentItem[] =>
  expandedThreadIds.has(comment.id) ? (comment.replies ?? []) : [];

export const toggleExpandedReplyThread = (
  expandedThreadIds: ReadonlySet<string>,
  commentId: string,
): Set<string> => {
  const next = new Set(expandedThreadIds);
  if (next.has(commentId)) next.delete(commentId);
  else next.add(commentId);
  return next;
};

/** Removes an old auto-inserted mention when a member replied to themselves. */
export const stripSelfReplyMention = (
  text: string | null | undefined,
  replyTo: string | null | undefined,
  authorName: string,
): string => {
  const commentText = text ?? '';
  const replyToken = toMentionToken(replyTo);
  const authorToken = toMentionToken(authorName);
  if (!replyToken || replyToken.toLowerCase() !== authorToken.toLowerCase()) {
    return commentText;
  }

  const mention = `@${replyToken}`;
  if (!commentText.toLowerCase().startsWith(mention.toLowerCase())) {
    return commentText;
  }

  const nextCharacter = commentText.charAt(mention.length);
  if (nextCharacter && !/\s/.test(nextCharacter)) return commentText;

  return commentText.slice(mention.length).trimStart();
};
